<script lang="ts" setup>
import { defineProps, defineEmits, ref, onMounted } from "vue";
import BaseModal from "@/components/base/BaseModal.vue";
import { getLocalStorageParams, setLocalStorageParams } from "@/utils/common";
import router from "@/router";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { useI18n } from "vue-i18n";

defineProps({
  showModal: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(["close"]);

const { t } = useI18n();

const activePaymentOption = ref<"card" | "sber">("card");

const closeModal = () => emits("close");
const selectMethods = () => {
  setLocalStorageParams("paymentMethod", activePaymentOption.value);
  closeModal();
  router.push("payment");
};

onMounted(() => {
  if (getLocalStorageParams("paymentMethod")) {
    activePaymentOption.value = getLocalStorageParams("paymentMethod") as
      | "card"
      | "sber";
  }
});
</script>
<template>
  <BaseModal
    v-if="showModal"
    :title="t('payment.modal.title')"
    :main-btn-title="t('payment.modal.continue')"
    @action="selectMethods"
    @close="closeModal"
  >
    <div class="paymentOptions">
      <div class="paymentOptions-item" @click="activePaymentOption = 'card'">
        <div class="paymentOptions-item-icon">
          <BaseFigureForIcon
            icon="card-pay"
            color="grey-secondary"
            size="medium"
          />
        </div>
        <div class="paymentOptions-item-text">
          <p class="h3">{{ t("payment.modal.cardTitle") }}</p>
          <p class="caption-small">{{ t("payment.modal.cardSubtitle") }}</p>
        </div>
        <div
          v-if="activePaymentOption === 'sber'"
          class="paymentOptions-item-btn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" fill="#CCD3DC" />
            <circle cx="12" cy="12" r="6" fill="#F2F4F6" />
          </svg>
        </div>
        <div
          v-if="activePaymentOption === 'card'"
          class="paymentOptions-item-btn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" fill="#CCD3DC" />
            <circle cx="12" cy="12" r="6" fill="#2E3066" />
          </svg>
        </div>
      </div>
      <div class="paymentOptions-item" @click="activePaymentOption = 'sber'">
        <div class="paymentOptions-item-icon">
          <BaseFigureForIcon
            icon="sber-pay"
            folder="payments"
            color="grey-secondary"
            is-original-color
            size="medium"
          />
        </div>
        <div class="paymentOptions-item-text">
          <p class="h3">{{ t("payment.modal.otherMethodTitle") }}</p>
          <p class="caption-small">{{ t("payment.modal.sberSubtitle") }}</p>
        </div>
        <div
          v-if="activePaymentOption === 'card'"
          class="paymentOptions-item-btn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" fill="#CCD3DC" />
            <circle cx="12" cy="12" r="6" fill="#F2F4F6" />
          </svg>
        </div>
        <div
          v-if="activePaymentOption === 'sber'"
          class="paymentOptions-item-btn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" fill="#CCD3DC" />
            <circle cx="12" cy="12" r="6" fill="#2E3066" />
          </svg>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.paymentOptions {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &-item {
    padding: 22px 16px;
    background-color: var(--color-white);
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    &-btn {
      margin-left: auto;
    }
  }
}
</style>
